import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@moe/oss/ui/tooltip";
import { cn } from "@moe/priv/utils";
import { useClickAway } from "@uidotdev/usehooks";
import { motion, useAnimationFrame, useMotionValue } from "motion/react";
import { useRef, useState } from "react";

interface Props {
  className?: string;
  src: string;
  title: string;
  description: string;
  size?: "sm" | "md" | "lg";
}

const sizes = {
  sm: "h-16 w-16",
  md: "h-28 w-28",
  lg: "h-32 w-32"
};

export function InteractiveBadge({ className, src, size = "md", title, description }: Props) {
  const [isPressed, setPressed] = useState(false);
  const [isHovered, setHovered] = useState(false);
  const autoRotateX = useMotionValue(0);
  const autoRotateY = useMotionValue(0);
  const time = useRef(0);

  useAnimationFrame((t) => {
    time.current = t / 1000;
    const radius = 20;
    const speed = 1;
    const upTiltBias = 5;
    autoRotateX.set(Math.sin(time.current * speed) * radius + upTiltBias);
    autoRotateY.set(Math.cos(time.current * speed) * radius);
  });

  const colors = {
    base: "#E8E8E8",
    highlight: "#FFFFFF",
    shadow: "#C0C0C0",
    accent: "#D8D8D8"
  };

  const layers = Array.from({ length: 10 }, (_, i) => ({
    depth: i,
    isEdge: i === 0 || i === 9
  }));

  const ref = useClickAway(() => {
    setPressed(false);
  });

  return (
    <div className={cn("flex items-center justify-center", className)}>
      <TooltipProvider>
        <Tooltip open={isPressed || isHovered}>
          <TooltipTrigger asChild>
            <div
              className="relative p-4"
              ref={ref as any}
              style={{
                perspective: "1000px",
                transformStyle: "preserve-3d"
              }}
            >
              <motion.div
                className="relative cursor-pointer focus:outline-none"
                initial={{
                  rotateX: 12,
                  rotateY: 20
                }}
                style={{
                  rotateX: autoRotateX,
                  rotateY: autoRotateY,
                  transformStyle: "preserve-3d"
                }}
                whileTap={{ scale: 0.93 }}
                onClick={() => setPressed((prev) => !prev)}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                transition={{
                  type: "easeOut",
                  duration: 0.25
                }}
              >
                {layers.map(({ depth, isEdge }) => (
                  <motion.div
                    key={depth}
                    className={`absolute inset-0 ${sizes[size]}
`}
                    style={{
                      transform: `translateZ(${-depth * 2}px)`,
                      background: `radial-gradient(circle at 40% 40%, 
                        ${colors.highlight}, 
                        ${isEdge ? colors.shadow : colors.base} 80%
                      )`,
                      borderRadius: "50%",
                      opacity: isEdge ? 1 : 0.9 - depth * 0.05,
                      boxShadow: isEdge ? `0 0 15px ${colors.shadow}80, inset 0 0 10px ${colors.shadow}` : "none"
                    }}
                  />
                ))}

                <div className={`relative ${sizes[size]}`} style={{ transform: "translateZ(2px)" }}>
                  <img
                    src={src}
                    draggable="false"
                    alt={title}
                    style={{
                      WebkitTouchCallout: "none",
                      WebkitUserSelect: "none",
                      userSelect: "none"
                    }}
                    className="h-full w-full select-none rounded-full object-cover"
                  />
                </div>
              </motion.div>
            </div>
          </TooltipTrigger>
          <TooltipContent className="p-4">
            <div className="space-y-2">
              <h3 className="font-bold">{title}</h3>
              <p className="text-muted-foreground w-64 text-sm">{description}</p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
